import { X } from "lucide-react";
import { useState } from "react";

import { useExperienceBands, useSaveExperienceBands, type ExperienceBandIn } from "@/api/queries";
import { useCan } from "@/auth/member";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Callout, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";

/**
 * Experience -> difficulty (DESIGN §17.5). The table `/pick` reads on every call, so a save here
 * shapes the next pick with no deploy. The last row has no upper limit — it catches everyone above
 * the row before it, which is why it cannot be removed or given a number. The API validates the
 * table; this mirrors the rules so a bad edit is flagged before Save.
 */
type Row = ExperienceBandIn;

const strip = (b: ExperienceBandIn): Row => ({
  max_years: b.max_years ?? null,
  min_difficulty: b.min_difficulty,
  max_difficulty: b.max_difficulty,
  secondary_min_difficulty: b.secondary_min_difficulty,
  secondary_max_difficulty: b.secondary_max_difficulty,
});

type DifficultyKey =
  "min_difficulty" | "max_difficulty" | "secondary_min_difficulty" | "secondary_max_difficulty";

const COLUMNS: { key: DifficultyKey; head: string; aria: string }[] = [
  { key: "min_difficulty", head: "Primary from", aria: "from" },
  { key: "max_difficulty", head: "to", aria: "to" },
  { key: "secondary_min_difficulty", head: "Secondary from", aria: "secondary from" },
  { key: "secondary_max_difficulty", head: "to", aria: "secondary to" },
];

function problems(rows: Row[]): string[] {
  const out: string[] = [];
  rows.forEach((r, i) => {
    if (r.max_years != null && i > 0) {
      const prev = rows[i - 1]?.max_years;
      if (prev != null && r.max_years <= prev) out.push("Years must increase from one row to the next.");
    }
    if (r.min_difficulty > r.max_difficulty || r.secondary_min_difficulty > r.secondary_max_difficulty)
      out.push("A row's “from” cannot be above its “to”.");
    if (r.secondary_min_difficulty > r.min_difficulty || r.secondary_max_difficulty > r.max_difficulty)
      out.push("A secondary range cannot be harder than the primary range on the same row.");
  });
  return [...new Set(out)];
}

function label(rows: Row[], i: number): string {
  const r = rows[i];
  if (!r) return "";
  if (r.max_years != null) return `Up to ${r.max_years} years`;
  return i === 0 ? "Any experience" : `More than ${rows[i - 1]?.max_years} years`;
}

const clamp = (v: string, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(Number(v) || lo)));

export function ExperienceBandsPanel() {
  const canManage = useCan("manage_picker");
  const saved = useExperienceBands();
  const save = useSaveExperienceBands();
  const [draft, setDraft] = useState<Row[] | null>(null);

  const server = saved.data?.bands.map(strip) ?? [];
  const rows = draft ?? server;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(server);
  const issues = problems(rows);

  function edit(i: number, patch: Partial<Row>) {
    setDraft(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  function addRow() {
    const last = rows.at(-1);
    if (!last) return;
    const finite = rows.slice(0, -1);
    const next = (finite.at(-1)?.max_years ?? 0) + 2;
    setDraft([
      ...finite,
      {
        max_years: next,
        min_difficulty: 1,
        max_difficulty: 5,
        secondary_min_difficulty: 1,
        secondary_max_difficulty: 5,
      },
      last,
    ]);
  }

  return (
    <Panel>
      <PanelHeader title="Difficulty by experience" />
      <div className="space-y-3 p-4">
        <p className="text-bank-muted text-sm">
          How hard the picker asks at each experience level (1 easiest – 5 hardest). Primary applies to the
          candidate's main skills; secondary applies to the stacks a request marks as secondary, so a weaker
          skill is asked an easier range. A save applies to the next pick; within each bucket the questions
          come back easy to hard.
        </p>
        <ErrorNote error={saved.error} />
        <table className="w-full max-w-2xl text-sm">
          <thead className="text-bank-muted text-left font-mono text-[10.5px] tracking-[0.07em] uppercase">
            <tr>
              <th className="py-1 font-medium">Candidate has</th>
              {COLUMNS.map((c) => (
                <th key={c.key} className="py-1 font-medium">
                  {c.head}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const open = r.max_years == null;
              return (
                <tr key={i} className="border-bank-border border-t">
                  <td className="py-1.5 pr-3">
                    {open || !canManage ? (
                      label(rows, i)
                    ) : (
                      <span className="flex items-center gap-1.5">
                        Up to
                        <Input
                          aria-label={`Row ${i + 1} up to years`}
                          className="w-16"
                          type="number"
                          min={0}
                          max={99}
                          value={r.max_years ?? ""}
                          onChange={(e) => edit(i, { max_years: clamp(e.target.value, 0, 99) })}
                        />
                        years
                      </span>
                    )}
                  </td>
                  {COLUMNS.map(({ key: k, aria }) => (
                    <td key={k} className="py-1.5 pr-3">
                      {canManage ? (
                        <Input
                          aria-label={`Row ${i + 1} ${aria}`}
                          className="w-16"
                          type="number"
                          min={1}
                          max={5}
                          value={r[k]}
                          onChange={(e) => edit(i, { [k]: clamp(e.target.value, 1, 5) })}
                        />
                      ) : (
                        <span className="font-mono">{r[k]}</span>
                      )}
                    </td>
                  ))}
                  <td className="py-1.5 text-right">
                    {canManage && !open && (
                      <button
                        type="button"
                        aria-label={`Remove row ${i + 1}`}
                        onClick={() => setDraft(rows.filter((_, j) => j !== i))}
                        className="text-bank-muted hover:text-bank-bad-ink rounded p-1 transition-colors"
                      >
                        <X className="size-3.5" aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {issues.map((m) => (
          <Callout key={m} tone="bad">
            {m}
          </Callout>
        ))}
        <ErrorNote error={save.error} />

        {canManage ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <Button size="sm" onClick={addRow} disabled={rows.length === 0}>
              Add row
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!dirty || save.isPending}
              onClick={() => {
                setDraft(null);
                save.reset();
              }}
            >
              Discard
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!dirty || issues.length > 0 || save.isPending}
              onClick={() => save.mutate(rows, { onSuccess: () => setDraft(null) })}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            {saved.data?.bands[0]?.updated_by && (
              <span className="text-bank-muted ml-2 font-mono text-[11px]">
                last changed by {saved.data.bands[0].updated_by}
              </span>
            )}
          </div>
        ) : (
          <p className="text-bank-muted text-xs">Only admins can change this table.</p>
        )}
      </div>
    </Panel>
  );
}
